import React, { useEffect } from 'react';
import './StudyWorkflowMenu.css';

export type WorkflowActionKey =
  | 'viewer'
  | 'edit'
  | 'image-fusion'
  | 'distortion-correction'
  | 'object-management'
  | 'smartbrush'
  | 'stereotaxy'
  | 'lead-localization';

export type WorkflowIconName =
  | 'viewer'
  | 'edit'
  | 'fusion'
  | 'distortion'
  | 'object'
  | 'smartbrush'
  | 'stereotaxy'
  | 'lead';

interface WorkflowStudy {
  id: string;
  patientName: string;
}

interface WorkflowItem {
  key: WorkflowActionKey;
  label: string;
  icon: WorkflowIconName;
}

interface WorkflowSection {
  key: string;
  title: string;
  items: WorkflowItem[];
}

interface StudyWorkflowMenuProps {
  study: WorkflowStudy;
  onClose: () => void;
  onSelectAction: (action: WorkflowActionKey) => void;
}

const WORKFLOW_SECTIONS: WorkflowSection[] = [
  {
    key: 'viewing',
    title: 'VIEWING',
    items: [
      { key: 'viewer', label: 'Viewer', icon: 'viewer' },
      { key: 'edit', label: 'Orientation', icon: 'edit' },
    ],
  },
  {
    key: 'co-registration',
    title: 'CO-REGISTRATION',
    items: [
      { key: 'image-fusion', label: 'Image Fusion', icon: 'fusion' },
      {
        key: 'distortion-correction',
        label: 'Distortion Correction Cranial',
        icon: 'distortion',
      },
    ],
  },
  {
    key: 'data-enrichment',
    title: 'DATA ENRICHMENT',
    items: [
      { key: 'object-management', label: 'Object Management', icon: 'object' },
      { key: 'smartbrush', label: 'SmartBrush', icon: 'smartbrush' },
    ],
  },
  {
    key: 'planning',
    title: 'PLANNING',
    items: [{ key: 'stereotaxy', label: 'Stereotaxy', icon: 'stereotaxy' }],
  },
  {
    key: 'post-op-review',
    title: 'POST-OP REVIEW',
    items: [{ key: 'lead-localization', label: 'Lead Localization', icon: 'lead' }],
  },
];

interface WorkflowIconProps {
  name: WorkflowIconName;
}

export const WorkflowIcon: React.FC<WorkflowIconProps> = ({ name }) => {
  const commonProps = {
    className: 'workflow-menu-item-icon',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.65,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (name) {
    case 'viewer':
      return (
        <svg {...commonProps}>
          <rect
            x="3.5"
            y="4"
            width="13"
            height="13"
            rx="1.6"
          />
          <path d="M8.5 9.5h3v3" />
          <path d="M11.5 9.5l-4.5 4.5" />
          <path d="M16.5 8h4v12h-12v-3" />
        </svg>
      );

    case 'edit':
      return (
        <svg {...commonProps}>
          <path d="m4 20 4.25-1 10.4-10.4a2.12 2.12 0 0 0-3-3L5.25 16 4 20Z" />
          <path d="m13.8 7.45 3 3" />
        </svg>
      );

    case 'fusion':
      return (
        <svg {...commonProps}>
          <path d="M12 3l8 4-8 4-8-4z" />
          <path d="M4 11l8 4 8-4" />
          <path d="M4 15l8 4 8-4" />
          <path
            d="M8 6.9l8 4M16 6.9l-8 4"
            opacity=".55"
          />
        </svg>
      );

    case 'distortion':
      return (
        <svg {...commonProps}>
          <path d="M4 6l8-3 8 3-8 3z" />
          <path d="M4 10l8-3 8 3-8 3z" />
          <path d="M4 14l8-3 8 3-8 3z" />
          <path d="M4 18l8-3 8 3-8 3z" />
        </svg>
      );

    case 'object':
      return (
        <svg {...commonProps}>
          <circle
            cx="9.5"
            cy="8.5"
            r="4"
          />
          <path d="M12.5 11.5l6.5 6.5" />
          <path d="M15 14l2-2 3 3-2 2" />
          <path d="M5 19c1.3-2.2 3.2-3.4 5.5-3.4" />
        </svg>
      );

    case 'smartbrush':
      return (
        <svg {...commonProps}>
          <path d="M14 4l6 6-8.5 8.5a4 4 0 01-5.7-5.7z" />
          <path d="M13 5l6 6" />
          <circle
            cx="5"
            cy="7"
            r=".7"
            fill="currentColor"
            stroke="none"
          />
          <circle
            cx="8"
            cy="4"
            r=".7"
            fill="currentColor"
            stroke="none"
          />
          <circle
            cx="4"
            cy="11"
            r=".7"
            fill="currentColor"
            stroke="none"
          />
        </svg>
      );

    case 'stereotaxy':
      return (
        <svg {...commonProps}>
          <path d="M5.5 17a7 7 0 1111-5.7" />
          <path d="M4.5 20h15" />
          <path d="M12 5v8" />
          <circle
            cx="12"
            cy="13"
            r="2"
          />
          <path d="M18 4v7M16 6h4" />
        </svg>
      );

    case 'lead':
      return (
        <svg {...commonProps}>
          <path d="M4.5 13c1.2-5.5 5.2-8.5 9.5-7 3.8 1.3 5.5 5.4 3.8 9" />
          <path d="M7 16c2-1.3 4.6-1.3 7 0" />
          <path d="M15 4l-3 9M9 13l6 2" />
          <circle
            cx="12"
            cy="13"
            r="1.8"
          />
        </svg>
      );

    default:
      return null;
  }
};

const StudyWorkflowMenu: React.FC<StudyWorkflowMenuProps> = ({
  study,
  onClose,
  onSelectAction,
}) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="workflow-menu-backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="workflow-menu-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-menu-title"
        aria-describedby="workflow-menu-description"
      >
        <header className="workflow-menu-header">
          <div className="workflow-menu-header-copy">
            <h2
              id="workflow-menu-title"
              className="workflow-menu-title"
            >
              Select Workflow
            </h2>
            <p
              id="workflow-menu-description"
              className="workflow-menu-subtitle"
            >
              {study.patientName}
            </p>
          </div>

          <button
            type="button"
            className="workflow-menu-close-button"
            onClick={onClose}
            aria-label="Close workflow menu"
            title="Close"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="workflow-menu-content">
          <div className="workflow-menu-grid">
            {WORKFLOW_SECTIONS.map(section => (
              <section
                key={section.key}
                className="workflow-menu-section"
              >
                <div className="workflow-menu-section-heading">
                  <h3 className="workflow-menu-heading">{section.title}</h3>
                </div>

                <div className="workflow-menu-items">
                  {section.items.map(item => (
                    <button
                      key={item.key}
                      type="button"
                      className="workflow-menu-item"
                      onClick={() => onSelectAction(item.key)}
                      aria-label={`${item.label} modülünü aç`}
                      title={item.label}
                    >
                      <span className="workflow-menu-item-icon-shell">
                        <WorkflowIcon name={item.icon} />
                      </span>

                      <span className="workflow-menu-item-copy">
                        <span className="workflow-menu-item-label">{item.label}</span>
                      </span>

                      <svg
                        className="workflow-menu-item-arrow"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          d="M9 6l6 6-6 6"
                          strokeWidth={1.8}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudyWorkflowMenu;
